import React from 'react';
import { CommonCard } from '@/components/common/CommonCard';
import {
    ShoppingBag, Clock, Truck, AlertTriangle, XCircle,
    Store, WifiOff, DollarSign, TrendingDown,
    Banknote, RotateCcw, ShoppingCart, Star, Repeat, Zap,
    UserPlus,
    CarFront
} from 'lucide-react';
import { formatRand } from '@/utility/helper';
import { CommonCardSkeleton } from '@/components/ui/Loaders/DashboardSkeletons';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { RoutesLink } from '@/utility/RouteLinks';

interface IKPIData {
    totalOrdersToday: number;
    activeOrders: number;
    pendingDeliveries: number;
    delayedOrders: number;
    failedOrders: number;
    activeStores: number;
    driverRequest: number;
    storesOffline: number;
    driversAvailable: string | number;
    driversBusy?: number;
    pendingPayout?: number;
    grossEarning: number;
    netEarning: number;
    pendingCod: number;
    totalRefundedAmount: number;
    totalStorePaidAmount: number;
    totalDriverPaidAmount: number
    // customerSatisfactionScore: number;
    // repeatCustomerPercent: number;
    // peakActiveHour: string;
}

interface Props {
    data?: IKPIData | null;
    loading?: boolean;
    activeFilter?: string;
}

export const BusinessKPISection: React.FC<Props> = ({ data, loading = false, activeFilter }) => {
    const navigate = useRoleNavigate();

    if (loading) {
        return (
            <div className="space-y-4">
                {/* Critical Operational */}
                <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
                        Critical Operational
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <CommonCardSkeleton key={i} />
                        ))}
                    </div>
                </div>

                {/* Financial */}
                <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                        Financial
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <CommonCardSkeleton key={i} />
                        ))}
                    </div>
                </div>

                {/* Informational */}
                {/* <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-blue-400"></span>
                        Informational
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <CommonCardSkeleton key={i} />
                        ))}
                    </div>
                </div> */}
            </div>
        );
    }

    const defaultData: IKPIData = {
        driverRequest: 0,
        totalOrdersToday: 0,
        activeOrders: 0,
        pendingDeliveries: 0,
        delayedOrders: 0,
        failedOrders: 0,
        activeStores: 0,
        storesOffline: 0,
        driversAvailable: '0',
        grossEarning: 0,
        netEarning: 0,
        pendingCod: 0,
        totalRefundedAmount: 0,
        totalStorePaidAmount: 0,
        totalDriverPaidAmount: 0,
        pendingPayout: 0,
        // customerSatisfactionScore: 0,
        // repeatCustomerPercent: 0,
        // peakActiveHour: 'N/A',
    };

    const activeData = data || defaultData;

    const getTimeRangeLabel = (filter?: string) => {
        switch (filter) {
            case 'today':
                return 'Today';
            case 'this_week':
                return 'This Week';
            case 'this_month':
                return 'This Month';
            case 'custom':
                return 'Selected Range';
            case '':
                return 'All Time';
            default:
                return 'Today';
        }
    };

    const timeRangeLabel = getTimeRangeLabel(activeFilter);

    return (
        <div className="space-y-4">
            {/* Critical Operational */}
            <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
                    Critical Operational
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
                    <CommonCard
                        label={`Orders ${timeRangeLabel}`}
                        value={activeData?.totalOrdersToday}
                        icon={<ShoppingBag />}
                        iconColor="#3B82F6"
                        onClick={() => navigate(RoutesLink.Orders)}
                    />
                    <CommonCard
                        label="Active Orders"
                        value={activeData?.activeOrders}
                        icon={<Clock />}
                        iconColor="#8B5CF6"
                        onClick={() => navigate(RoutesLink.Orders)}
                    />
                    <CommonCard
                        label="Pending Delivery"
                        value={activeData?.pendingDeliveries}
                        icon={<Truck />}
                        iconColor="#F59E0B"
                        onClick={() => navigate(RoutesLink.Orders)}
                    />
                    <CommonCard
                        label="Delayed Orders"
                        value={activeData?.delayedOrders}
                        icon={<AlertTriangle />}
                        iconColor="#EF4444"
                        onClick={() => navigate(RoutesLink.Orders)}
                    />
                    <CommonCard
                        label="Failed Orders"
                        value={activeData?.failedOrders}
                        icon={<XCircle />}
                        iconColor="#DC2626"
                        onClick={() => navigate(RoutesLink.Orders)}
                    />
                    <CommonCard
                        label="Active Stores"
                        value={activeData?.activeStores}
                        icon={<Store />}
                        iconColor="#10B981"
                        onClick={() => navigate(RoutesLink.stores)}
                    />
                    <CommonCard
                        label="Pending Driver"
                        value={activeData?.driverRequest}
                        icon={<CarFront />}
                        iconColor="#10B981"
                        onClick={() => navigate(RoutesLink.Drivers)}
                    />
                    <CommonCard
                        label="Stores Offline"
                        value={activeData?.storesOffline}
                        icon={<WifiOff />}
                        iconColor="#6B7280"
                        onClick={() => navigate(RoutesLink.stores)}
                    />
                    <CommonCard
                        label="Drivers Available"
                        value={`${activeData?.driversAvailable}`}
                        icon={<CarFront />}
                        iconColor="#06B6D4"
                        onClick={() => navigate(RoutesLink.Drivers)}
                    />
                    <CommonCard
                        label="Pending Payouts"
                        value={activeData?.pendingPayout}
                        icon={<Banknote />}
                        iconColor="#06B6D4"
                        onClick={() => navigate(RoutesLink.payouts)}
                    />
                </div>
            </div>

            {/* Financial */}
            <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                    Financial
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                    <CommonCard
                        label={`Gross Earning ${timeRangeLabel}`}
                        value={formatRand(activeData?.grossEarning)}
                        icon={<DollarSign />}
                        iconColor="#10B981"
                    // onClick={() => navigate(RoutesLink.Analytics)}
                    />
                    <CommonCard
                        label="Net Earnings"
                        value={formatRand(activeData?.netEarning)}
                        icon={<TrendingDown />}
                        iconColor="#059669"
                    // onClick={() => navigate(RoutesLink.Analytics)}
                    />
                    <CommonCard
                        label="COD Pending"
                        value={formatRand(activeData?.pendingCod)}
                        icon={<Banknote />}
                        iconColor="#F59E0B"
                    // onClick={() => navigate(RoutesLink.CashSettlement)}
                    />
                    <CommonCard
                        label={`Refunds ${timeRangeLabel}`}
                        value={formatRand(activeData?.totalRefundedAmount)}
                        icon={<RotateCcw />}
                        iconColor="#EF4444"
                    // onClick={() => navigate(RoutesLink.Return)}
                    />
                    <CommonCard
                        label="Total Store Paid Amount"
                        value={formatRand(activeData?.totalStorePaidAmount)}
                        icon={<ShoppingCart />}
                        iconColor="#6366F1"
                    // onClick={() => navigate(RoutesLink.Analytics)}
                    />
                    <CommonCard
                        label="Total Driver Paid Amount"
                        value={formatRand(activeData?.totalDriverPaidAmount)}
                        icon={<ShoppingCart />}
                        iconColor="#6366F1"
                    // onClick={() => navigate(RoutesLink.Analytics)}
                    />
                </div>
            </div>

            {/* Informational */}
            {/* <div>
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-blue-400"></span>
                    Informational
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <CommonCard
                        label="Customer Satisfaction"
                        value={`${activeData?.customerSatisfactionScore}/5 ⭐`}
                        icon={<Star />}
                        iconColor="#FBBF24"
                        onClick={() => navigate(RoutesLink.Reviews)}
                    />
                    <CommonCard
                        label="Repeat Customers"
                        value={`${activeData?.repeatCustomerPercent}%`}
                        icon={<Repeat />}
                        iconColor="#8B5CF6"
                        onClick={() => navigate(RoutesLink.Customers)}
                    />
                    <CommonCard
                        label="Peak Active Hour"
                        value={activeData?.peakActiveHour}
                        icon={<Zap />}
                        iconColor="#F97316"
                    />
                </div>
            </div> */}
        </div>
    );
};
